import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { Timeoff } from "../../../types";
import { Palmtree, ChevronRight, Loader2 } from "lucide-react";
import { setSelectedTimeoffForView } from "../../../redux/features/timeoff";
import { useNavigate } from "react-router";
import { getApprovalStatusCheck } from "../../../utils/timeoff";
import { DateTimeUtil } from "../../../utils";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";

const ViewAllUpcomingTimeOff: React.FC = () => {
    const { isLoadingTimeOffRequests, fetchedTimeoffRequests } = useSelector((state: RootState) => state.timeoff);
    const today = DateTimeUtil.formatDate(new Date(), "yyyy-MM-dd");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { setSourcePath } = useNavigationHistory();

    const [upcomingRequests, setUpcomingRequests] = useState<Timeoff[]>([]);

    useEffect(() => {
        if (!fetchedTimeoffRequests) return;
        const upcoming = fetchedTimeoffRequests
            .filter((request) => {
                if (!request.start_date || !request.end_date) return false;

                const isFutureStart = DateTimeUtil.IsAfterWithDayjsUtc(request.start_date, today);
                const isFutureEnd = DateTimeUtil.IsAfterWithDayjsUtc(request.end_date, today);
                const isOngoing =
                    DateTimeUtil.IsBeforeWithDayjsUtc(request.start_date, today) &&
                    DateTimeUtil.IsAfterWithDayjsUtc(request.end_date, today);

                return (isFutureStart || isFutureEnd) && !isOngoing;
            })
            .sort((a, b) =>
                DateTimeUtil.DifferenceInDaysWithDayjsUtc(b.start_date!, a.start_date!)
            );

        setUpcomingRequests(upcoming);
    }, [fetchedTimeoffRequests]);
    
    // Set up navigation history
    useEffect(() => {
        // Set source path for back navigation
        setSourcePath("/m-timeoff");
        
        // Set up global back handler
        setGlobalBackHandler(() => {
            navigate("/m-timeoff");
            return true;
        });
        
        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [navigate, setSourcePath]);

    const formatDateRange = (start: string, end: string): string =>
        DateTimeUtil.formatDateRangeFancy(start, end);

    const navToTimeoffDetails = (timeoff: Timeoff) => {
        dispatch(setSelectedTimeoffForView(timeoff));
        navigate("/m-timeoff-details");
    };

    return (
        <section className="mb-6">
            {isLoadingTimeOffRequests ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
            ) : upcomingRequests.length > 0 ? (
                <div className="space-y-1">
                    {upcomingRequests.map((request) => (
                        <div
                            key={request.time_off_request_id}
                            className="flex items-center justify-between p-3 cursor-pointer border-b border-stone-200"
                            onClick={() => navToTimeoffDetails(request)}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="p-2 flex items-center justify-center">
                                    <Palmtree className="text-green-600 w-6 h-6" />
                                </div>
                                <div>
                                    <p className="text-gray-900 font-medium">
                                        {formatDateRange(request.start_date!, request.end_date!)}
                                    </p>
                                    <div className="flex items-center space-x-1 text-gray-600 text-sm">
                                        {getApprovalStatusCheck(request.status!)}{" "}
                                        <span>{request.hours_requested} {request.track_time_in}</span>
                                    </div>
                                </div>
                            </div>
                            <ChevronRight className="text-gray-400 w-5 h-5" />
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-gray-500">You have no upcoming time off.</p>
            )}
        </section>
    );
};

export default ViewAllUpcomingTimeOff;
